import {Abilities} from '@casl/ability';
import {AbilityActionsForSubject, AbilitySubjects} from './ability-types';

export type AccessManagementFieldType = 'string' | 'number' | 'boolean' | 'enum' | 'id' | 'email' | 'date';

/**
 * All condition operator types in display order, grouped by category.
 * This is the single source of truth — {@link ConditionOperatorType} is
 * derived from it so the union and the runtime array never diverge.
 *
 * MongoDB `$`-prefixed keys are used directly for all operators that map to a MongoDB operator.
 * The two special cases that have no MongoDB operator counterpart use non-`$` strings:
 *
 * - `ref-eq` — field === runtime account context value (e.g. ${account.tenantId}), stored as a plain string
 * - `$eq`    — field === literal value, stored as a plain value (implicit MongoDB equality)
 * - `$ne`    — field !== literal value
 * - `$gte`   — field >= literal number
 * - `$lte`   — field <= literal number
 * - `$gt`    — field >  literal number
 * - `$lt`    — field <  literal number
 * - `$exists`— field existence check
 * - `$in`    — field value is in a list
 * - `$all`   — field array contains all listed values
 * - `$nin`   — field value is not in a list
 */
export const CONDITION_OPERATOR_TYPES = [
  'ref-eq',
  '$eq',
  '$ne',
  '$gte',
  '$lte',
  '$gt',
  '$lt',
  '$exists',
  '$in',
  '$all',
  '$nin',
] as const;

/** Discriminant for the type of condition operator used in a dynamic resource condition row. */
export type ConditionOperatorType = (typeof CONDITION_OPERATOR_TYPES)[number];

export interface AccessManagementFieldConfig {
  name: string;
  type: AccessManagementFieldType;
  enumValues?: readonly string[];
  children?: readonly AccessManagementFieldConfig[];
}

export interface AccessManagementSubjectConfig<TActions extends string> {
  actions: readonly TActions[];
  fields?: readonly AccessManagementFieldConfig[];
  /**
   * When true, this subject is excluded from UI displays (e.g. permissions matrix).
   * The subject remains fully functional for CASL ability checks and policies.
   */
  hidden?: boolean;
}

/**
 * Strongly-typed access management configuration for a given Abilities union.
 *
 * Keys are constrained to the concrete subject strings derived from TAbilities.
 * The actions array for each subject is constrained to the valid actions for
 * that subject as declared in the abilities union — invalid action/subject
 * combinations are caught at compile time.
 *
 * @template TAbilities - The CASL Abilities union for the product (e.g. SampleAppAbilities)
 */
export type AccessManagementConfig<TAbilities extends Abilities> = {
  [S in Extract<AbilitySubjects<TAbilities>, string>]?: AccessManagementSubjectConfig<
    Extract<AbilityActionsForSubject<TAbilities, S>, string>
  >;
};

/** Operators that are valid per field type. */
export const ALLOWED_OPERATORS_BY_TYPE: Record<AccessManagementFieldType, ReadonlySet<ConditionOperatorType>> = {
  string: new Set<ConditionOperatorType>(['ref-eq', '$eq', '$ne', '$exists', '$in', '$nin']),
  number: new Set<ConditionOperatorType>(['$eq', '$ne', '$gte', '$lte', '$gt', '$lt', '$exists', '$in', '$nin']),
  boolean: new Set<ConditionOperatorType>(['$eq', '$exists']),
  enum: new Set<ConditionOperatorType>(['$eq', '$ne', '$exists', '$in', '$nin']),
  id: new Set<ConditionOperatorType>(['ref-eq', '$eq', '$ne', '$exists', '$in', '$nin']),
  email: new Set<ConditionOperatorType>(['ref-eq', '$eq', '$ne', '$exists', '$in', '$nin']),
  date: new Set<ConditionOperatorType>(['$eq', '$ne', '$gte', '$lte', '$gt', '$lt', '$exists']),
};

/** Operator types that use a single scalar value ($eq, ref-eq, $ne, numeric comparisons). */
export const SCALAR_OPERATOR_TYPES = new Set<ConditionOperatorType>([
  'ref-eq',
  '$eq',
  '$ne',
  '$gte',
  '$lte',
  '$gt',
  '$lt',
]);
/** Operator types that use an array / list of values. */
export const ARRAY_OPERATOR_TYPES = new Set<ConditionOperatorType>(['$in', '$all', '$nin']);
/** Operator types that require a numeric value. */
export const NUMERIC_OPERATOR_TYPES = new Set<ConditionOperatorType>(['$gte', '$lte', '$gt', '$lt']);

/**
 * Access management config subject names whose fields are available as
 * `${subject.field}` placeholders in permission conditions at runtime.
 *
 * Used by both the frontend (`conditions-form`) to populate the value
 * dropdown, and by the backend (`CaslAbilityFactory` / `PermissionValidatorService`)
 * to resolve and validate placeholders. Extend this list when additional
 * runtime context objects are supported.
 */
export const INTERPOLATION_SUBJECTS = ['Account'] as const;

/**
 * Converts a `Subject.field` dot-path (e.g. `"Account.id"`) into the
 * `${subject.field}` placeholder format expected by the CASL runtime resolver.
 *
 * The first segment (the subject name) is lower-cased; the rest is left as-is.
 *   "Account.id"       → "${account.id}"
 *   "Account.tenantId" → "${account.tenantId}"
 *   "User.email"       → "${user.email}"
 */
export function toRefPlaceholder(dotPath: string): string {
  const dotIndex = dotPath.indexOf('.');
  if (dotIndex === -1) return dotPath; // unexpected format — return unchanged
  const subject = dotPath.slice(0, dotIndex).toLowerCase();
  const field = dotPath.slice(dotIndex + 1);
  return `\${${subject}.${field}}`;
}

/**
 * Converts a `${subject.field}` placeholder back to the `Subject.field`
 * dot-path used in the form value dropdown (inverse of {@link toRefPlaceholder}).
 *
 * The subject segment is title-cased to match PascalCase subject names in the
 * access management config.
 *   "${account.id}"       → "Account.id"
 *   "${account.tenantId}" → "Account.tenantId"
 *   "${user.email}"       → "User.email"
 */
export function fromRefPlaceholder(placeholder: string): string {
  const inner = placeholder.slice(2, -1); // strip ${ and }
  const dotIndex = inner.indexOf('.');
  if (dotIndex === -1) return placeholder;
  const subject = inner.slice(0, dotIndex);
  const field = inner.slice(dotIndex + 1);
  return `${subject.charAt(0).toUpperCase()}${subject.slice(1)}.${field}`;
}

/**
 * Recursively walks an AccessManagementFieldConfig tree and returns
 * a flat array of dot-path strings for all leaf fields.
 *
 * ```ts
 * flattenFieldPaths([{name:'metadata', children:[{name:'createdBy'}]}])
 * // => ['metadata.createdBy']
 * ```
 */
export function flattenFieldPaths(fields: readonly AccessManagementFieldConfig[], prefix = ''): string[] {
  return fields.flatMap(f => {
    const path = prefix ? `${prefix}.${f.name}` : f.name;
    return f.children?.length ? flattenFieldPaths(f.children, path) : [path];
  });
}

/**
 * Walks the field config tree and returns the leaf {@link AccessManagementFieldConfig}
 * that matches the given dot-separated path, or `undefined` if not found.
 *
 * ```ts
 * getFieldConfig(fields, 'metadata.createdBy')
 * // => { name: 'createdBy', type: 'string', label: 'Created By' }
 * ```
 */
export function getFieldConfig(
  fields: readonly AccessManagementFieldConfig[],
  path: string
): AccessManagementFieldConfig | undefined {
  const [head, ...rest] = path.split('.');
  const node = fields.find(f => f.name === head);
  if (!node) return undefined;
  if (rest.length === 0) return node;
  return node.children?.length ? getFieldConfig(node.children, rest.join('.')) : undefined;
}
